import { useState, useMemo } from "react";
import { Plus, Pencil, Trash2, ArrowLeftRight, Coins, ArrowRight } from "lucide-react";
import { useFinancialState } from "@/context/StateContext";
import { fmtMoney, fmtDate, uid, toMonthly, merchantKey, adjustAccountsForTx, EXPENSE_CATEGORIES, INCOME_CATEGORIES, todayISO } from "@/lib/finance";
import { periodKey } from "@/lib/life";
import { periodLabel } from "@/lib/wellness";
import { usePeriod } from "@/context/PeriodContext";
import { PeriodNav } from "@/components/PeriodNav";
import { PageHeader, SectionCard, Money, EmptyState, AddButton, KpiCard } from "@/components/shared";
import { FormModal, TextField, NumberField, SelectField, TextArea } from "@/components/forms";
import { IncomeSpendBars } from "@/components/Charts";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";

const TX_TYPES = [
  { value: "income", label: "Income" },
  { value: "expense", label: "Expense" },
  { value: "transfer", label: "Transfer" },
];
const FREQ = [
  { value: "monthly", label: "Monthly" },
  { value: "biweekly", label: "Bi-weekly" },
  { value: "weekly", label: "Weekly" },
  { value: "yearly", label: "Yearly" },
];
const typeChip = {
  income: "bg-[color:rgba(31,115,85,0.12)] text-[var(--green)]",
  expense: "bg-[color:rgba(180,35,24,0.10)] text-[var(--danger)]",
  transfer: "bg-[color:rgba(198,139,60,0.16)] text-[#8a5a17]",
};

export default function CashFlow() {
  const { state, update, loading } = useFinancialState();
  const { horizon, setHorizon, date, setDate } = usePeriod();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [incOpen, setIncOpen] = useState(false);
  const [incForm, setIncForm] = useState(null);

  const monthly = useMemo(() => {
    if (!state) return [];
    const buckets = {};
    (state.transactions || []).forEach((t) => {
      const key = (t.date || "").slice(0, 7);
      if (!key) return;
      buckets[key] = buckets[key] || { income: 0, spending: 0 };
      if (t.type === "income") buckets[key].income += Number(t.amount || 0);
      else if (t.type === "expense") buckets[key].spending += Math.abs(Number(t.amount || 0));
    });
    return Object.keys(buckets).sort().slice(-6).map((k) => ({
      label: new Date(k + "-01T12:00:00").toLocaleDateString("en-US", { month: "short" }),
      income: Math.round(buckets[k].income),
      spending: Math.round(buckets[k].spending),
    }));
  }, [state]);

  if (loading || !state) return <Skeleton className="h-96 rounded-[14px]" />;

  const accounts = state.accounts || [];
  const acctOpts = accounts.map((a) => ({ value: a.id, label: a.name }));
  const acctName = (id) => accounts.find((a) => a.id === id)?.name || "";

  const pLabel = periodLabel(horizon, date);
  const filtered = (state.transactions || []).filter((t) => t.date && periodKey(horizon, t.date) === periodKey(horizon, date));
  const income = filtered.filter((t) => t.type === "income").reduce((s, t) => s + Number(t.amount || 0), 0);
  const spending = filtered.filter((t) => t.type === "expense").reduce((s, t) => s + Math.abs(Number(t.amount || 0)), 0);
  const transfers = filtered.filter((t) => t.type === "transfer").reduce((s, t) => s + Math.abs(Number(t.amount || 0)), 0);
  const net = income - spending;
  const monthlyIncome = (state.incomeSources || []).reduce((s, x) => s + toMonthly(x.amount, x.frequency), 0);

  const catOpts = (type) => (type === "income" ? INCOME_CATEGORIES : EXPENSE_CATEGORIES).map((c) => ({ value: c, label: c }));

  const openNew = () => {
    setForm({
      id: uid(), date: todayISO(), name: "", type: "expense",
      category: "", accountId: accounts[0]?.id || "", fromAccountId: accounts[0]?.id || "",
      toAccountId: accounts[1]?.id || accounts[0]?.id || "", amount: "", notes: "",
    });
    setOpen(true);
  };
  const openEdit = (t) => setForm({ notes: "", accountId: "", fromAccountId: "", toAccountId: "", ...t, amount: Math.abs(Number(t.amount || 0)) }) || setOpen(true);

  const save = () => {
    if (!form.name) return toast.error("Description is required");
    if (!form.amount || Number(form.amount) <= 0) return toast.error("Enter an amount greater than 0");
    const isTransfer = form.type === "transfer";
    if (isTransfer && form.fromAccountId === form.toAccountId) return toast.error("Choose two different accounts");

    let amt = Number(form.amount || 0);
    if (form.type === "expense") amt = -Math.abs(amt);
    else amt = Math.abs(amt);

    const tx = {
      ...form,
      amount: amt,
      category: isTransfer ? "Transfer" : form.category || "Other",
      account: isTransfer ? `${acctName(form.fromAccountId)} → ${acctName(form.toAccountId)}` : acctName(form.accountId),
    };

    update((prev) => {
      const existing = (prev.transactions || []).find((x) => x.id === tx.id);
      // Reverse the old effect, then apply the new one, so balances stay correct on edit
      let acc = prev.accounts || [];
      if (existing) acc = adjustAccountsForTx(acc, existing, -1);
      acc = adjustAccountsForTx(acc, tx, +1);

      const list = existing ? prev.transactions.map((x) => (x.id === tx.id ? tx : x)) : [tx, ...(prev.transactions || [])];
      list.sort((a, b) => (b.date || "").localeCompare(a.date || ""));

      // Smart Categories: learn category corrections
      let categoryRules = prev.categoryRules || {};
      const mkey = merchantKey(tx.name);
      if (!isTransfer && mkey && tx.category && (!existing || existing.category !== tx.category)) {
        categoryRules = { ...categoryRules, [mkey]: tx.category };
      }
      return { ...prev, transactions: list, accounts: acc, categoryRules };
    }, `Saved transaction: ${tx.name}`);
    setOpen(false);
    toast.success(isTransfer ? "Transfer recorded — balances updated" : "Transaction saved");
  };

  const remove = (id) => {
    update((prev) => {
      const existing = (prev.transactions || []).find((x) => x.id === id);
      const acc = existing ? adjustAccountsForTx(prev.accounts || [], existing, -1) : prev.accounts;
      return { ...prev, transactions: prev.transactions.filter((x) => x.id !== id), accounts: acc };
    }, "Deleted transaction");
    toast.success("Deleted — balances updated");
  };

  // income sources
  const openIncNew = () => { setIncForm({ id: uid(), name: "", amount: "", frequency: "monthly" }); setIncOpen(true); };
  const openIncEdit = (i) => { setIncForm({ ...i }); setIncOpen(true); };
  const saveInc = () => {
    if (!incForm.name) return toast.error("Name is required");
    const src = { ...incForm, amount: Number(incForm.amount || 0) };
    update((prev) => {
      const exists = (prev.incomeSources || []).some((x) => x.id === src.id);
      return { ...prev, incomeSources: exists ? prev.incomeSources.map((x) => (x.id === src.id ? src : x)) : [...(prev.incomeSources || []), src] };
    }, `Saved income source: ${src.name}`);
    setIncOpen(false); toast.success("Income source saved");
  };
  const removeInc = (id) => update((prev) => ({ ...prev, incomeSources: prev.incomeSources.filter((x) => x.id !== id) }), "Deleted income source");

  const editing = form && (state.transactions || []).some((x) => x.id === form.id);

  return (
    <div>
      <PageHeader title="Cash flow" subtitle="Track income, spending, and transfers — transfers move money between your accounts." testid="cashflow-header"
        actions={<AddButton onClick={openNew} testid="add-transaction-button"><Plus className="h-4 w-4 mr-1.5" /> Add transaction</AddButton>} />

      <div className="mb-4">
        <PeriodNav horizon={horizon} setHorizon={setHorizon} date={date} setDate={setDate} testid="cashflow-period" />
      </div>

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        <KpiCard label="Income" value={fmtMoney(income)} sub={pLabel} icon={Coins} testid="cf-income" />
        <KpiCard label="Spending" value={fmtMoney(spending)} sub={pLabel} testid="cf-spending" />
        <KpiCard label="Net" value={fmtMoney(net)} sub={net >= 0 ? "Surplus" : "Deficit"} accent={net < 0} testid="cf-net" />
        <KpiCard label="Transfers" value={fmtMoney(transfers)} sub={pLabel} icon={ArrowLeftRight} testid="cf-transfers" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-7">
          <SectionCard title="Transactions" subtitle={`${filtered.length} in this period`} testid="transactions-card">
            {filtered.length ? (
              <div className="max-h-[460px] overflow-auto">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Description</TableHead>
                    <TableHead className="hidden sm:table-cell">Category</TableHead>
                    <TableHead className="hidden md:table-cell">Account</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead></TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {filtered.map((t) => (
                      <TableRow key={t.id} data-testid={`transactions-table-row-${t.id}`}>
                        <TableCell>
                          <div className="font-medium flex items-center gap-2">{t.name}
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full capitalize ${typeChip[t.type] || ""}`}>{t.type}</span>
                          </div>
                          <div className="text-[11px] text-[var(--text-faint)]">{fmtDate(t.date)}</div>
                        </TableCell>
                        <TableCell className="hidden sm:table-cell text-[var(--text-muted)]">{t.category}</TableCell>
                        <TableCell className="hidden md:table-cell text-[var(--text-muted)] text-xs">{t.account}</TableCell>
                        <TableCell className="text-right"><Money value={t.type === "transfer" ? -Math.abs(t.amount) : t.amount} decimals={2} className={t.type === "transfer" ? "text-[#8a5a17]" : ""} /></TableCell>
                        <TableCell className="text-right whitespace-nowrap">
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(t)} data-testid={`edit-tx-${t.id}`} aria-label="Edit"><Pencil className="h-3.5 w-3.5" /></Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-[var(--danger)]" onClick={() => remove(t.id)} data-testid={`delete-tx-${t.id}`} aria-label="Delete"><Trash2 className="h-3.5 w-3.5" /></Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : <EmptyState icon={ArrowLeftRight} title="No transactions" message="Add a transaction or import a CSV in Data & settings." action={<AddButton onClick={openNew}><Plus className="h-4 w-4 mr-1.5" />Add transaction</AddButton>} />}
          </SectionCard>
        </div>
        <div className="lg:col-span-5 space-y-4">
          <SectionCard title="Income vs spending" subtitle="Last 6 months" testid="income-spend-card">
            {monthly.length ? <IncomeSpendBars data={monthly} /> : <EmptyState icon={Coins} title="Not enough data" message="Add transactions to see trends." />}
          </SectionCard>
          <SectionCard title="Income sources" right={<Button variant="ghost" size="sm" onClick={openIncNew} data-testid="add-income-button" className="text-[var(--green)]"><Plus className="h-4 w-4 mr-1" />Add</Button>} subtitle={`${fmtMoney(monthlyIncome)}/mo total`} testid="income-sources-card">
            {(state.incomeSources || []).length ? (
              <div className="space-y-2">
                {state.incomeSources.map((i) => (
                  <div key={i.id} className="flex items-center justify-between rounded-[10px] border border-[color:var(--border-subtle)] bg-white px-3 py-2" data-testid={`income-row-${i.id}`}>
                    <div><div className="text-sm font-medium">{i.name}</div><div className="text-[11px] text-[var(--text-faint)] capitalize">{i.frequency}</div></div>
                    <div className="flex items-center gap-1"><Money value={i.amount} className="text-sm mr-1" />
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openIncEdit(i)} aria-label="Edit"><Pencil className="h-3 w-3" /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-[var(--danger)]" onClick={() => removeInc(i.id)} aria-label="Delete"><Trash2 className="h-3 w-3" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : <EmptyState icon={Coins} title="No income sources" message="Add your salary and other income." />}
          </SectionCard>
        </div>
      </div>

      {/* Transaction modal */}
      {form && (
        <FormModal open={open} onOpenChange={setOpen} title={editing ? "Edit transaction" : "Add transaction"}
          description={form.type === "transfer" ? "Move money between two of your accounts — balances update automatically." : "Categorize it and choose which account it hits."} onSubmit={save} testid="transaction-modal">
          <SelectField label="Type" value={form.type} onChange={(v) => setForm({ ...form, type: v, category: "" })} options={TX_TYPES} testid="tx-type-select" />
          <NumberField label="Amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} testid="tx-amount-input" />
          <TextField label="Description" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} full testid="tx-name-input" placeholder={form.type === "transfer" ? "e.g. Move to savings" : "e.g. Whole Foods"} />

          {form.type !== "transfer" ? (
            <>
              <SelectField label="Category" value={form.category} onChange={(v) => setForm({ ...form, category: v })} options={catOpts(form.type)} testid="tx-category-select" />
              <SelectField label="Account" value={form.accountId} onChange={(v) => setForm({ ...form, accountId: v })} options={acctOpts.length ? acctOpts : [{ value: "", label: "No accounts — add one first" }]} testid="tx-account-select" />
            </>
          ) : (
            <>
              <SelectField label="From account" value={form.fromAccountId} onChange={(v) => setForm({ ...form, fromAccountId: v })} options={acctOpts} testid="tx-from-account-select" />
              <SelectField label="To account" value={form.toAccountId} onChange={(v) => setForm({ ...form, toAccountId: v })} options={acctOpts} testid="tx-to-account-select" />
            </>
          )}

          <TextField label="Date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} testid="tx-date-input" />
          <TextArea label="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} full rows={2} placeholder="Optional details…" testid="tx-notes-input" />
        </FormModal>
      )}

      {incForm && (
        <FormModal open={incOpen} onOpenChange={setIncOpen} title={(state.incomeSources || []).some((x) => x.id === incForm.id) ? "Edit income source" : "Add income source"} onSubmit={saveInc} testid="income-modal">
          <TextField label="Name" value={incForm.name} onChange={(e) => setIncForm({ ...incForm, name: e.target.value })} full testid="income-name-input" />
          <NumberField label="Amount" value={incForm.amount} onChange={(e) => setIncForm({ ...incForm, amount: e.target.value })} testid="income-amount-input" />
          <SelectField label="Frequency" value={incForm.frequency} onChange={(v) => setIncForm({ ...incForm, frequency: v })} options={FREQ} testid="income-freq-select" />
        </FormModal>
      )}
    </div>
  );
}
